/* eslint-disable react-hooks/exhaustive-deps */
import * as React from "react";
import Container from "@mui/material/Container";
import {
    FormControl,
    MenuItem,
    Select,
    InputLabel,
    Checkbox,
    ListItemText,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    Snackbar,
    Alert,
} from "@mui/material";
import { useData } from "../../providers/DataProvider";
import { BonusProps } from "../../providers/types";
import { useState, useMemo, useCallback } from "react";
import RoleModal from "../../modals/RoleModal";
import moment from "moment";
import { SelectChangeEvent } from "@mui/material/Select";
import {
    MagnifyingGlassIcon,
    XCircleIcon,
    InformationCircleIcon,
    CheckBadgeIcon
} from "@heroicons/react/24/outline";
import { reviewPageStatusSortRank } from "../../utils/reviewPageStatusOrder";
import ReviewApprovalsTable, {
    Decision,
} from "../../components/Tables/ReviewApprovalsTable";
import httpClient from "../../utils/httpClient";

interface BulkEditBonusItem {
    jobId: number;
    bonusStatus: "Approved" | "Rejected";
    comment?: string;
}

interface BulkEditBonusResult {
    jobId: number;
    success: boolean;
    data?: string | null;
    error?: string | null;
}

const BULK_MAX_ITEMS = 100; // must match EditBonusesEndpoint.MaxItems

const postBulkBonuses = async (items: BulkEditBonusItem[]): Promise<BulkEditBonusResult[]> => {
    const res = await httpClient.post("/api/cba/bonuses/bulk", items);
    return res.data;
};

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export default function ReviewApprovals() {
    const {
        regions,
        bonuses,
        loadBonuses,
        role,
        loadingBonuses,
        selectedRegion,
        setSelectedRegion,
    } = useData();

    const [open, setOpen] = React.useState<boolean>(false);
    const [selectedStatusType, setSelectedStatusType] = React.useState<
        number | string | null
    >(() => {
        const saved = localStorage.getItem("cba.selectedStatusReview");
        return saved ? JSON.parse(saved) : "All";
    });
    const [selectedCommunities, setSelectedCommunities] = useState<string[]>(
        () => {
            const saved = localStorage.getItem("cba.selectedCommunitiesReview");
            return saved ? JSON.parse(saved) : [];
        }
    );
    const [searchTerm, setSearchTerm] = useState("");
    const [isFocused, setIsFocused] = useState(false);

    // ── Approval workflow state (keyed by jobId) ──────────────────────────────
    const [decisions, setDecisions] = useState<Record<number, Decision>>({});
    const [isAutoSelecting, setIsAutoSelecting] = useState(false);
    const [isPosting, setIsPosting] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [pendingPayload, setPendingPayload] = useState<BulkEditBonusItem[]>([]);
    const [notification, setNotification] = useState<{
        open: boolean;
        message: string;
        severity: "success" | "error" | "warning" | "info";
    }>({
        open: false,
        message: "",
        severity: "info",
    });

    const showNotification = (
        message: string,
        severity: "success" | "error" | "warning" | "info" = "info"
    ) => {
        setNotification({ open: true, message, severity });
    };

    React.useEffect(() => {
        if (role) {
            loadBonuses(role);
        }
    }, [role]);

    // Seed decisions from any server-side prior decision when bonuses (re)load
    React.useEffect(() => {
        const seed: Record<number, Decision> = {};
        bonuses.forEach((b) => {

            if (b.isAutoApprovalEligible === undefined) b.isAutoApprovalEligible = false;

            if (b.isApproved != null || (b.rejectionReason && b.rejectionReason.trim())) {
                seed[b.jobId] = {
                    isApproved: b.isApproved ?? null,
                    rejectionReason: b.rejectionReason ?? "",
                };
            }
        });
        setDecisions(seed);
    }, [bonuses]);

    // ── Filter handlers (same as existing Review) ────────────────────────────

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
    };

    const clearSearch = () => {
        setSearchTerm("");
    };

    const handleStatusTypeChange = (event: any) => {
        const value = event.target.value;
        setSelectedStatusType(value);
        localStorage.setItem("cba.selectedStatusReview", JSON.stringify(value));
    };

    const handleCommunityChange = (event: SelectChangeEvent<string[]>) => {
        const value = event.target.value;
        if (value.includes("clear-all")) {
            setSelectedCommunities([]);
            localStorage.setItem("cba.selectedCommunitiesReview", JSON.stringify([]));
        } else {
            setSelectedCommunities(typeof value === "string" ? [value] : value);
            localStorage.setItem(
                "cba.selectedCommunitiesReview",
                JSON.stringify(typeof value === "string" ? [value] : value)
            );
        }
    };

    const getUniqueCommunities = () => {
        const filteredBonuses =
            selectedRegion && selectedRegion.regionId !== -1
                ? bonuses.filter(
                    (bonus) => bonus.regionName === selectedRegion.regionName
                )
                : bonuses;

        const communities = filteredBonuses
            .map((bonus) => bonus.subdivision)
            .filter((subdivision): subdivision is string => !!subdivision);

        return ["All", ...Array.from(new Set(communities)).sort((a, b) => a.localeCompare(b))];
    };

    const formatStatus = (status: number, bonus?: BonusProps): string => {
        const isDefaultDate = (date: string | Date) =>
            moment(date).isSame(moment("0001-01-01"));

        switch (status) {
            case 0: return "Not Started";
            case 1: return "In Upload Process";
            case 2: return "PM Review";
            case 3: return "SPM Review";
            case 4: return "DM Review";
            case 5: return "Ops Review";
            case 6: return "Ops Approved";
            case 7: return "Rejected";
            case 8: return "Busted";
            case 9:
                return bonus?.payrollProcessedOn && !isDefaultDate(bonus.payrollProcessedOn)
                    ? `In Payroll Process (${moment(bonus.payrollProcessedOn).format("MM/DD/YYYY")})`
                    : "In Payroll Process";
            case 10:
                return bonus?.payrollPaidOn && !isDefaultDate(bonus.payrollPaidOn)
                    ? `Paid (${moment(bonus.payrollPaidOn).format("MM/DD/YYYY")})`
                    : "Paid";
            default: return "Unknown Status";
        }
    };

    // ── Filtered bonuses (scope for counts + auto-select) ─────────────────────

    const filteredBonuses = useMemo(() => {
        let result = bonuses;

        if (selectedRegion && selectedRegion.regionId !== -1) {
            result = result.filter((b) => b.regionName === selectedRegion.regionName);
        }
        if (selectedCommunities.length > 0) {
            result = result.filter((b) => selectedCommunities.includes(b.subdivision || ""));
        }
        if (selectedStatusType !== "All" && selectedStatusType !== null) {
            result = result.filter((b) => b.statusTypeId === selectedStatusType);
        }
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            result = result.filter(
                (b) =>
                    (b.jobAddress && b.jobAddress.toLowerCase().includes(term)) ||
                    (b.jobNumber && b.jobNumber.toLowerCase().includes(term)) ||
                    (b.subdivision && b.subdivision.toLowerCase().includes(term)) ||
                    (b.builderName && b.builderName.toLowerCase().includes(term))
            );
        }

        return result;
    }, [bonuses, selectedRegion, selectedCommunities, selectedStatusType, searchTerm]);

    // ── Decision helpers ─────────────────────────────────────────────────────

    const getDecision = useCallback(
        (jobId: number): Decision => {
            const bonus = bonuses.find((b) => b.jobId === jobId && b.statusTypeId === 5);
            return (
                decisions[jobId] ?? {
                    isApproved: bonus?.isApproved ?? null,
                    rejectionReason: bonus?.rejectionReason ?? "",
                }
            );
        },
        [decisions, bonuses]
    );

    const handleApprove = useCallback((jobId: number, isApproved: boolean | null) => {
        setDecisions((prev) => ({
            ...prev,
            [jobId]: {
                isApproved,
                rejectionReason: isApproved ? "" : prev[jobId]?.rejectionReason ?? "",
            },
        }));
    }, []);

    const handleReason = useCallback((jobId: number, reason: string) => {
        setDecisions((prev) => ({
            ...prev,
            [jobId]: {
                isApproved: prev[jobId]?.isApproved ?? false,
                rejectionReason: reason,
            },
        }));
    }, []);

    // ── Auto-Select Eligible Jobs ────────────────────────────────────────────
    // Approves every visible job flagged isAutoSelectEligible by the endpoint,
    // without overriding a decision the user already made.

    const handleAutoSelect = useCallback(async () => {
        setIsAutoSelecting(true);
        try {
            setDecisions((prev) => {
                const next = { ...prev };
                filteredBonuses.forEach((b) => {
                    if (b.isAutoApprovalEligible) {
                        const cur = next[b.jobId];
                        if (!cur || cur.isApproved == null) {
                            next[b.jobId] = { isApproved: true, rejectionReason: "" };
                        }
                    }
                });
                return next;
            });
        } finally {
            setIsAutoSelecting(false);
        }
    }, [filteredBonuses]);


    const handleClearSelection = () => {
        setDecisions({});
    };

    const handlePostApprovals = useCallback(() => {
        // Rejections must have a reason
        const rejectionsWithoutReason = filteredBonuses.filter((b) => {
            const d = getDecision(b.jobId);
            return d.isApproved === false && !d.rejectionReason.trim();
        });

        if (rejectionsWithoutReason.length > 0) {
            showNotification(
                `Please provide a rejection reason for ${rejectionsWithoutReason.length} ${rejectionsWithoutReason.length === 1 ? "job" : "jobs"}.`,
                "warning"
            );
            return;
        }

        const decided = filteredBonuses.filter((b) => {
            const d = getDecision(b.jobId);
            return (
                d.isApproved === true ||
                (d.isApproved === false && d.rejectionReason.trim() !== "")
            );
        });

        if (decided.length === 0) {
            showNotification("No jobs have an approve/reject decision.", "warning");
            return;
        }

        const payload: BulkEditBonusItem[] = decided
            .filter((b) => {
                const d = getDecision(b.jobId);
                return b.statusTypeId === 5 && d.isApproved !== null;
            })
            .map((b) => {
                const d = getDecision(b.jobId);
                return {
                    jobId: b.jobId,
                    bonusStatus: d.isApproved === true ? "Approved" : "Rejected",
                    comment: d.rejectionReason || undefined,
                };
            });

        setPendingPayload(payload);
        setConfirmOpen(true);
    }, [filteredBonuses, getDecision]);

    const confirmPostApprovals = async () => {
        setConfirmOpen(false);
        setIsPosting(true);
        const results: BulkEditBonusResult[] = [];

        try {
            for (let i = 0; i < pendingPayload.length; i += BULK_MAX_ITEMS) {
                results.push(...(await postBulkBonuses(pendingPayload.slice(i, i + BULK_MAX_ITEMS))));
            }

            const failed = results.filter((r) => !r.success);

            showNotification(
                failed.length === 0
                    ? `Successfully posted ${results.length} ${results.length === 1 ? "decision" : "decisions"}.`
                    : `Posted ${results.length - failed.length} of ${results.length}. Failed:\n` +
                    failed.map((f) => `Job ${f.jobId}: ${f.error}`).join("\n"),
                failed.length === 0 ? "success" : "error"
            );
        } catch (err) {
            console.error("Bulk post failed", err);
            showNotification(
                `Posting failed after ${results.length} of ${pendingPayload.length} job(s).`,
                "error"
            );
        } finally {
            setIsPosting(false);
            setPendingPayload([]);
            if (role) loadBonuses(role);
        }
    };

    // ── Counts ───────────────────────────────────────────────────────────────

    const approvedCount = useMemo(
        () => filteredBonuses.filter((b) => getDecision(b.jobId).isApproved === true && b.statusTypeId === 5).length,
        [filteredBonuses, getDecision]
    );

    const rejectedCount = useMemo(
        () =>
            filteredBonuses.filter((b) => {
                const d = getDecision(b.jobId);
                return d.isApproved === false  && b.statusTypeId === 5 && d.rejectionReason.trim() !== "";
            }).length,
        [filteredBonuses, getDecision]
    );



    // ── Render ───────────────────────────────────────────────────────────────

    return (
        <Container
            component="main"
            maxWidth={false}
            sx={{
                height: "calc(100vh - 56px)",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                pt: 2,
                pb: 2,
            }}
        >
            <div className="flex-grow flex flex-col overflow-hidden bg-white rounded-lg shadow-md">
                {/* ── Filters ─────────────────────────────────────────────────── */}
                <div className="p-4 flex-shrink-0">
                    <div className="flex flex-col sm:flex-row justify-between gap-3">
                        <div className="flex flex-row items-start gap-3 w-full md:w-3/5 lg:w-1/2">
                            <div className="relative w-full max-w-[300px]">
                                <div className="relative">
                                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                    <input
                                        type="text"
                                        className="w-full pl-10 pr-10 h-10 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
                                        placeholder="Search..."
                                        value={searchTerm}
                                        onChange={handleSearchChange}
                                        onFocus={() => setIsFocused(true)}
                                        onBlur={() => setTimeout(() => setIsFocused(false), 200)}
                                    />
                                    {searchTerm && (
                                        <button
                                            onClick={clearSearch}
                                            className="absolute right-3 top-1/2 transform -translate-y-1/2"
                                        >
                                            <XCircleIcon className="h-5 w-5 text-primary-500 hover:text-primary-700" />
                                        </button>
                                    )}
                                </div>
                            </div>
                            <FormControl sx={{ width: 300 }} size="small">
                                <InputLabel id="community-select-label">Community</InputLabel>
                                <Select
                                    labelId="community-select-label"
                                    size="small"
                                    label="Community"
                                    multiple
                                    value={selectedCommunities}
                                    onChange={handleCommunityChange}
                                    renderValue={(selected) => {
                                        const count = selected.length;
                                        if (count === 1) return selected[0];
                                        if (count > 1) return `${count} Communities Selected`;
                                        return (
                                            <span style={{ color: "rgba(0, 0, 0, 0.6)" }}>
                                                Select Communities
                                            </span>
                                        );
                                    }}
                                    sx={{ height: "40px" }}
                                >
                                    <MenuItem value="clear-all">
                                        <em>Clear All</em>
                                    </MenuItem>
                                    {getUniqueCommunities()
                                        .filter((c) => c !== "All")
                                        .map((community) => (
                                            <MenuItem key={community} value={community}>
                                                <Checkbox checked={selectedCommunities.indexOf(community) > -1} />
                                                <ListItemText primary={community} />
                                            </MenuItem>
                                        ))}
                                </Select>
                            </FormControl>
                            <FormControl sx={{ width: 300 }}>
                                <InputLabel id="status-select-label">Status</InputLabel>
                                <Select
                                    labelId="status-select-label"
                                    size="small"
                                    label="Status"
                                    value={selectedStatusType || ""}
                                    onChange={handleStatusTypeChange}
                                    sx={{ height: "40px" }}
                                >
                                    <MenuItem value="All">
                                        <em>All</em>
                                    </MenuItem>
                                    {bonuses
                                        .reduce((unique, bonus) => {
                                            if (!unique.some((obj) => obj.statusTypeId === bonus.statusTypeId)) {
                                                unique.push(bonus);
                                            }
                                            return unique;
                                        }, [] as typeof bonuses)
                                        .sort(
                                            (a, b) =>
                                                reviewPageStatusSortRank(a.statusTypeId) -
                                                reviewPageStatusSortRank(b.statusTypeId) ||
                                                a.statusTypeId - b.statusTypeId
                                        )
                                        .map((uniqueBonus) => (
                                            <MenuItem key={uniqueBonus.statusTypeId} value={uniqueBonus.statusTypeId}>
                                                {formatStatus(uniqueBonus.statusTypeId)}
                                            </MenuItem>
                                        ))}
                                </Select>
                            </FormControl>
                        </div>
                    </div>

                    {/* ── Action buttons + helper text ───────────────────────────── */}
                    <div className="mt-4 flex flex-col sm:flex-row items-start sm:items-center gap-3">
                        <div className="flex items-center gap-3">
                            <button
                                onClick={handleAutoSelect}
                                disabled={isAutoSelecting ||
                                    !filteredBonuses.some((b) => b.statusTypeId === 5 && b.isAutoApprovalEligible)}
                                className="
                                    inline-flex items-center gap-2
                                    px-4 py-2
                                    text-sm font-medium
                                    text-blue-700
                                    bg-white
                                    border border-blue-300
                                    hover:bg-blue-50
                                    disabled:text-gray-400
                                    disabled:bg-gray-50
                                    disabled:border-gray-200
                                    disabled:cursor-not-allowed
                                    rounded-md
                                    transition-colors"
                            >
                                {isAutoSelecting ? (
                                    <svg
                                        className="animate-spin w-4 h-4"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                        />
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z"
                                        />
                                    </svg>
                                ) : (
                                    <CheckBadgeIcon className="h-4 w-4" />
                                )}

                                Select eligible jobs
                            </button>

                            <button
                                onClick={handleClearSelection}
                                disabled={approvedCount === 0 && rejectedCount === 0}
                                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 disabled:text-gray-400 disabled:bg-gray-50 disabled:border-gray-200 disabled:cursor-not-allowed rounded-md transition-colors"
                            >
                                <i className="ti ti-x text-base" aria-hidden="true" />
                                Clear selection
                            </button>

                            <button
                                onClick={handlePostApprovals}
                                disabled={isPosting || (approvedCount === 0 && rejectedCount === 0)}
                                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed rounded-md transition-colors"
                            >
                                {isPosting ? (
                                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z" />
                                    </svg>
                                ) : (
                                    <i className="ti ti-wand text-base" aria-hidden="true" />
                                )}
                                Post selected jobs
                            </button>
                        </div>

                        <div className="flex items-center gap-4 text-xs text-gray-500">
                            <Tooltip
                                title={
                                    <div className="max-w-sm text-sm">
                                        <div className="font-semibold mb-2">
                                            Auto-approval eligibility
                                        </div>

                                        <div className="mb-2">
                                            For OpsPlus users, a job is selected when:
                                        </div>

                                        <ul className="list-disc pl-5 space-y-1">
                                            <li>It has at least one active builder and no inactive builders.</li>
                                            <li>Builder assignments have not changed since the last review.</li>
                                            <li>No employee appears more than once on the job.</li>
                                            <li>Builder splits and deduction amounts match the latest reviewed values.</li>
                                        </ul>

                                        <div className="mt-3 border-t pt-2">
                                            This only selects eligible jobs. Nothing is approved until
                                            <strong> Post selected jobs</strong> is clicked.
                                        </div>
                                    </div>
                                }
                                arrow
                            >
                                <span className="inline-flex items-center gap-1 cursor-help">
                                    <InformationCircleIcon className="w-4 h-4" />
                                    How are jobs auto-selected?
                                </span>
                            </Tooltip>
                            {(approvedCount > 0 || rejectedCount > 0) && (
                                <span>
                                    <span className="font-semibold text-green-600">{approvedCount}</span> approved
                                    &nbsp;&nbsp; · &nbsp;&nbsp;
                                    <span className="font-semibold text-red-600">{rejectedCount}</span> rejected
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {/* ── Table (accordion, same look as /review) ─────────────────── */}
                <div className="flex-grow overflow-auto relative">
                    <ReviewApprovalsTable
                        data={bonuses}
                        role={role}
                        loading={loadingBonuses}
                        searchTerm={searchTerm}
                        selectedStatusType={selectedStatusType}
                        selectedCommunities={selectedCommunities}
                        selectedRegion={selectedRegion}
                        decisions={decisions}
                        onApprove={handleApprove}
                        onReason={handleReason}
                    />
                </div>
            </div>
            <RoleModal open={open} setOpen={setOpen} />

            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 600 }}>Post Decisions</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        You’re about to post decisions for <strong>{pendingPayload.length} {pendingPayload.length === 1 ? "job" : "jobs"}</strong>.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button onClick={() => setConfirmOpen(false)} color="inherit">
                        Cancel
                    </Button>
                    <Button onClick={confirmPostApprovals} variant="contained" autoFocus>
                        Post Decisions
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={notification.open}
                autoHideDuration={4500}
                onClose={() => setNotification((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
                <Alert
                    severity={notification.severity}
                    variant="filled"
                    onClose={() => setNotification((prev) => ({ ...prev, open: false }))}
                    sx={{ width: "100%", whiteSpace: "pre-line" }}
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </Container>
    );
}